import * as React from 'react'
import { Form, FormInstance, Input, Select, Switch, Tooltip } from 'antd'
import { Drawer, executeCleanDrawerRender, Well } from '@pushly/aqe/lib/components'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc'
import { classnames } from '@halo/_utils/classnames'
import { FlagModel } from '@halo/models/flag.model'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { handleFormErrorFields, simpleNotification } from '@halo/_utils/notifications'
import { Rule } from 'rc-field-form/lib/interface'
import { PlatformFlagEntities } from '@halo/enums/platform-flag-entities'
import * as deepEqual from 'react-fast-compare'
import { QuestionCircleOutlined } from '@ant-design/icons'

interface IFlagsEditorProps {
    flag: FlagModel | Partial<FlagModel>
    mode?: 'create' | 'edit'
    onClose: (ev: any) => void
    onSubmit: () => void
    visible: boolean
}

interface IFlagsEditorState {}

export class FlagsEditor extends React.Component<IFlagsEditorProps, IFlagsEditorState> {
    protected appSvc: AppService
    protected formRef = React.createRef<FormInstance>()

    public constructor(props: IFlagsEditorProps) {
        super(props)

        this.appSvc = Container.get(AppService)

        this.state = {}

        executeCleanDrawerRender(this)
    }

    public render() {
        const flag = this.props.flag
        const defaults = flag ?? new FlagModel()
        const currentValues = this.getCurrentValues()

        return (
            <Drawer
                className={classnames('flags-editor')}
                closable={true}
                disableSubmit={Object.keys(currentValues).length === 0}
                getContainer={this.appSvc.getAppContainer}
                onClose={this.props.onClose}
                onSubmit={this.handleSubmit}
                placement="right"
                title={this.isEditMode() ? 'Edit Flag' : 'New Flag'}
                visible={this.props.visible}
            >
                <Form
                    {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                    className="hide-inline-error"
                    ref={this.formRef}
                    onValuesChange={() => this.forceUpdate()}
                >
                    <Well title="Flag Details" mode="ghost" hideFooter={true}>
                        <Form.Item
                            className="key"
                            initialValue={defaults.getKey()}
                            label="Key"
                            name="key"
                            rules={[
                                {
                                    required: true,
                                    validator: (_rule, value) => this.validateStringField(_rule, value, 'Key'),
                                },
                            ]}
                        >
                            <Input
                                disabled={this.isEditMode()}
                                size="small"
                                onChange={(ev) => {
                                    const key = ev.target.value.toUpperCase().replace(/\s/g, '_')
                                    this.form?.setFieldsValue({ key })
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            className="display-name"
                            initialValue={defaults.getDisplayName()}
                            label="Display Name"
                            name="displayName"
                            rules={[
                                {
                                    required: true,
                                    validator: (_rule, value) => this.validateStringField(_rule, value, 'Display Name'),
                                },
                            ]}
                        >
                            <Input
                                size="small"
                                onChange={(ev) => {
                                    this.form?.setFieldsValue({ displayName: ev.target.value })
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Description"
                            className="description"
                            name="description"
                            initialValue={defaults.getDescription()}
                        >
                            <Input
                                size="small"
                                onChange={(ev) => {
                                    this.form?.setFieldsValue({ description: ev.target.value })
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Applicable Entities"
                            className="app-entities"
                            hasFeedback={false}
                            name="applicableEntities"
                            initialValue={defaults.getApplicableEntities()}
                            rules={[
                                {
                                    required: true,
                                    validator: this.validateEntityArray,
                                },
                            ]}
                        >
                            <Select<PlatformFlagEntities[]>
                                mode="multiple"
                                onChange={(value) => {
                                    this.form?.setFieldsValue({
                                        applicableEntities: value,
                                    })

                                    if (
                                        !value.includes(PlatformFlagEntities.DOMAIN) &&
                                        this.form.getFieldValue('isSdkFlag') === true
                                    ) {
                                        this.form?.setFieldsValue({ isSdkFlag: false })

                                        simpleNotification(
                                            'warning',
                                            `SDK Flag was disabled because
                                        ${PlatformFlagEntities.DOMAIN.toUpperCase()} was removed from Applicable Entities.`,
                                            2,
                                        )
                                    }
                                }}
                                onDeselect={(val) => {
                                    this.form?.setFieldsValue({
                                        defaultForEntities: this.form
                                            ?.getFieldValue('defaultForEntities')
                                            ?.filter((v: string) => val !== v),
                                    })
                                }}
                                options={Object.values(PlatformFlagEntities).map((entity) => ({
                                    label: entity.toUpperCase(),
                                    value: entity,
                                }))}
                                size="small"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Default For Entities"
                            className="default-entities"
                            name="defaultForEntities"
                            initialValue={Object.values(PlatformFlagEntities)?.filter(
                                (entity) => flag?.getDefaultForEntities()?.find((val: string) => val === entity),
                            )}
                            validateTrigger="onSubmit"
                            rules={[
                                {
                                    validator: (_rule, value) => {
                                        if (value) {
                                            const applicableEntities: string[] =
                                                this.form?.getFieldValue('applicableEntities')
                                            if (applicableEntities?.length) {
                                                const validEntitiesMap: any = {}
                                                for (const ent of applicableEntities) {
                                                    if (!validEntitiesMap[ent]) {
                                                        validEntitiesMap[ent] = ent
                                                    }
                                                }

                                                for (const def of value) {
                                                    if (!validEntitiesMap[def]) {
                                                        return Promise.reject(
                                                            'Field Default For Entities is ' +
                                                                'not valid. It may only contain values which are found ' +
                                                                'in Applicable Entities.',
                                                        )
                                                    }
                                                }
                                            }

                                            return Promise.resolve()
                                        }
                                    },
                                },
                            ]}
                        >
                            <Select<PlatformFlagEntities[]>
                                mode="multiple"
                                onChange={(value) => {
                                    this.form?.setFieldsValue({ defaultForEntities: value })
                                }}
                                options={
                                    this.form
                                        ?.getFieldValue('applicableEntities')
                                        ?.map((entity: string) => ({ label: entity.toUpperCase(), value: entity })) ??
                                    []
                                }
                                size="small"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Active"
                            className="active"
                            name="isActive"
                            required={true}
                            initialValue={defaults.getIsActive() ?? false}
                            valuePropName={'checked'}
                        >
                            <Switch
                                defaultChecked={defaults.getIsActive()}
                                onChange={(value) => {
                                    this.form?.setFieldsValue({ isActive: value })
                                }}
                                size="small"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Dev Only"
                            name="isDevOnly"
                            required={true}
                            initialValue={defaults.getIsDevOnly() ?? false}
                            valuePropName={'checked'}
                        >
                            <Switch
                                defaultChecked={defaults.getIsDevOnly()}
                                onChange={(value) => {
                                    this.form?.setFieldsValue({ isDevOnly: value })
                                }}
                                size="small"
                            />
                        </Form.Item>

                        <Form.Item
                            initialValue={defaults.getIsSdkFlag() ?? false}
                            label="SDK Injected"
                            name="isSdkFlag"
                            required={true}
                            tooltip={{
                                title: 'The SDK Flag option is only available for domain flags.',
                                icon: <QuestionCircleOutlined className="sdk-flag-info" />,
                                className: 'sdk-flag-info',
                            }}
                            valuePropName={'checked'}
                        >
                            <Switch
                                defaultChecked={defaults.getIsSdkFlag()}
                                disabled={!this.formValue('applicableEntities')?.includes(PlatformFlagEntities.DOMAIN)}
                                onChange={(value) => {
                                    this.form?.setFieldsValue({ isSdkFlag: value })
                                }}
                                size="small"
                            />
                        </Form.Item>

                        {!this.isEditMode() && (
                            <Form.Item label="Apply Defaults to All Entities?" name="applyAll">
                                <Switch
                                    onChange={(value) => {
                                        this.form?.setFieldsValue({ applyAll: value })
                                    }}
                                    size="small"
                                />
                            </Form.Item>
                        )}
                    </Well>
                </Form>
            </Drawer>
        )
    }

    protected get form() {
        return this.formRef?.current
    }

    protected formValue(key: string) {
        return this.formRef?.current?.getFieldValue(key)
    }

    protected isEditMode() {
        return this.props.mode === 'edit'
    }

    protected getCurrentValues(): Partial<FlagModel> & { applyAll?: boolean } {
        const values: any = {}
        const formValues = this.form?.getFieldsValue()
        const flag = this.props.flag

        if (formValues?.key) {
            if (!deepEqual(formValues.key, flag.getKey())) {
                values.key = formValues.key.trim()
            }
        }

        if (formValues?.displayName) {
            if (!deepEqual(formValues.displayName, flag.getDisplayName())) {
                values.displayName = formValues.displayName.trim()
            }
        }

        let description = formValues?.description?.trim()
        if (description !== undefined) {
            if (description.trim() === '') {
                description = null
            }
            if (!deepEqual(description, flag.getDescription())) {
                values.description = description
            }
        }

        if (formValues?.applicableEntities) {
            if (!deepEqual(formValues.applicableEntities, flag.getApplicableEntities())) {
                values.applicableEntities = formValues.applicableEntities
            }
        }

        if (formValues?.defaultForEntities) {
            if (!deepEqual(formValues.defaultForEntities, flag.getDefaultForEntities())) {
                values.defaultForEntities = formValues.defaultForEntities
            }
        }

        if (formValues?.isActive !== undefined) {
            if (formValues.isActive !== flag.getIsActive()) {
                values.isActive = formValues.isActive
            }
        }

        if (formValues?.isDevOnly !== undefined) {
            if (formValues.isDevOnly !== flag.getIsDevOnly()) {
                values.isDevOnly = formValues.isDevOnly
            }
        }

        if (formValues?.isSdkFlag !== undefined) {
            if (formValues.isSdkFlag !== flag.getIsSdkFlag()) {
                values.isSdkFlag = formValues.isSdkFlag
            }
        }

        if (!this.isEditMode()) {
            if (formValues?.applyAll) {
                values.applyAll = formValues.applyAll
            }
        }

        return values
    }

    protected validateStringField = async (_rule: Rule, value: string, fieldName: string) => {
        if (!value?.trim()) {
            await Promise.reject(`Flag ${fieldName} is required.`)
        }

        await Promise.resolve()
    }

    protected validateEntityArray = async (_rule: Rule, value: PlatformFlagEntities[]) => {
        if (!value || value?.length === 0) {
            await Promise.reject(`Flag requires at least 1 Applicable Entity to be applied.`)
        }

        await Promise.resolve()
    }

    protected handleSubmit = async (): Promise<void> => {
        try {
            await this.form?.validateFields()
            const mergedValues = this.getCurrentValues()
            let submitComplete = false

            if (this.isEditMode()) {
                const updated = await this.appSvc.updateFlag(mergedValues, this.props.flag?.getKey(), {
                    showLoadingScreen: false,
                })

                if (updated.ok) {
                    submitComplete = true
                }
            } else {
                const options = {
                    query: {
                        applyAll: mergedValues.applyAll,
                    },
                }
                const created = await this.appSvc.createFlag(mergedValues, {
                    query: options.query,
                    showLoadingScreen: false,
                })

                if (created.ok) {
                    submitComplete = true
                }
            }

            if (submitComplete && this.props.onSubmit) {
                this.props.onSubmit()
            }
        } catch (error) {
            handleFormErrorFields(error)
        }
    }
}
